import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { FileText, Info, X } from "lucide-react";
import { SUPPLIER_DOC_TYPES } from "@/lib/constants";

const MAX_BYTES = 10 * 1024 * 1024; // 10 MB

/**
 * DocumentoProveedorDialog — Upload + captura de referencias externas + vínculos opcionales.
 * Recibe `suppliers` (lista para el selector). Opcionalmente `defaults` para pre-fill (folio, tipo, vínculos).
 */
export function DocumentoProveedorDialog({
  open, onOpenChange, onSaved, suppliers = [], tenantParam = {}, defaults = null,
}) {
  const [supplierId, setSupplierId] = useState("");
  const [docType, setDocType] = useState("factura");
  const [externalFolio, setExternalFolio] = useState("");
  const [externalSerie, setExternalSerie] = useState("");
  const [externalUuid, setExternalUuid] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [subtotal, setSubtotal] = useState("");
  const [iva, setIva] = useState("");
  const [total, setTotal] = useState("");
  const [creditedAmount, setCreditedAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [xmlFile, setXmlFile] = useState(null);
  const [purchaseOrderId, setPurchaseOrderId] = useState("");
  const [accountsPayableId, setAccountsPayableId] = useState("");
  const [purchaseReturnId, setPurchaseReturnId] = useState("");
  const [saving, setSaving] = useState(false);

  // Data lazy-loaded para selectores de vínculo (filtrados por proveedor)
  const [pos, setPos] = useState([]);
  const [aps, setAps] = useState([]);
  const [returns, setReturns] = useState([]);

  useEffect(() => {
    if (!open) return;
    setSupplierId(defaults?.supplier_id || "");
    setDocType(defaults?.doc_type || "factura");
    setExternalFolio(defaults?.external_folio || "");
    setExternalSerie(defaults?.external_serie || "");
    setExternalUuid(defaults?.external_uuid || "");
    setIssueDate(defaults?.issue_date || new Date().toISOString().slice(0, 10));
    setSubtotal("");
    setIva("");
    setTotal("");
    setCreditedAmount("");
    setNotes("");
    setPdfFile(null);
    setXmlFile(null);
    setPurchaseOrderId(defaults?.purchase_order_id || "");
    setAccountsPayableId(defaults?.accounts_payable_id || "");
    setPurchaseReturnId(defaults?.purchase_return_id || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaults?.supplier_id, defaults?.doc_type]);

  useEffect(() => {
    if (!supplierId) { setPos([]); setAps([]); setReturns([]); return; }
    let alive = true;
    (async () => {
      try {
        const [{ data: p }, { data: a }, { data: r }] = await Promise.all([
          api.get("/purchase-orders", { params: { ...tenantParam, supplier_id: supplierId } }),
          api.get("/accounts-payable", { params: { ...tenantParam, supplier_id: supplierId } }),
          api.get("/purchase-returns", { params: { ...tenantParam, supplier_id: supplierId } }),
        ]);
        if (!alive) return;
        setPos(Array.isArray(p) ? p : (p?.items || []));
        setAps(Array.isArray(a) ? a : (a?.items || []));
        setReturns(Array.isArray(r) ? r : (r?.items || []));
      } catch (_e) { /* silencioso — vínculos son opcionales */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId]);

  const isNC = docType === "nota_credito";

  const hasFile = pdfFile || xmlFile;

  function validateFileSize(f) {
    if (!f) return true;
    if (f.size > MAX_BYTES) {
      toast.error(`${f.name} excede 10 MB`);
      return false;
    }
    return true;
  }

  async function submit() {
    if (!supplierId) { toast.error("Selecciona un proveedor"); return; }
    if (!hasFile) { toast.error("Adjunta al menos un archivo (PDF o XML)"); return; }
    if (!validateFileSize(pdfFile) || !validateFileSize(xmlFile)) return;
    if (!externalFolio.trim() && docType !== "otro") {
      toast.error("Captura el folio externo del documento");
      return;
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("supplier_id", supplierId);
      fd.append("doc_type", docType);
      if (externalFolio.trim()) fd.append("external_folio", externalFolio.trim());
      if (externalSerie.trim()) fd.append("external_serie", externalSerie.trim());
      if (externalUuid.trim()) fd.append("external_uuid", externalUuid.trim());
      if (issueDate) fd.append("issue_date", issueDate);
      if (subtotal !== "") fd.append("subtotal", String(Number(subtotal) || 0));
      if (iva !== "") fd.append("iva", String(Number(iva) || 0));
      if (total !== "") fd.append("total", String(Number(total) || 0));
      if (isNC && creditedAmount !== "") fd.append("credited_amount", String(Number(creditedAmount) || 0));
      if (notes.trim()) fd.append("notes", notes.trim());
      if (purchaseOrderId) fd.append("purchase_order_id", purchaseOrderId);
      if (accountsPayableId) fd.append("accounts_payable_id", accountsPayableId);
      if (purchaseReturnId) fd.append("purchase_return_id", purchaseReturnId);
      if (pdfFile) fd.append("pdf_file", pdfFile);
      if (xmlFile) fd.append("xml_file", xmlFile);

      await api.post("/supplier-documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params: tenantParam,
      });
      toast.success("Documento guardado");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dlg-supplier-doc">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" /> Nuevo documento del proveedor
          </DialogTitle>
          <DialogDescription>
            Módulo administrativo. Los datos fiscales son solo referencia externa; no se valida CFDI ni se timbra.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          {/* Proveedor y tipo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Proveedor *</Label>
              <Select value={supplierId} onValueChange={setSupplierId}>
                <SelectTrigger data-testid="sd-select-supplier"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.trade_name || s.legal_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo de documento *</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger data-testid="sd-select-doctype"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SUPPLIER_DOC_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Referencias externas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Serie</Label>
              <Input value={externalSerie} onChange={(e) => setExternalSerie(e.target.value)} data-testid="sd-input-serie" placeholder="A" />
            </div>
            <div>
              <Label>Folio {docType !== "otro" ? "*" : ""}</Label>
              <Input value={externalFolio} onChange={(e) => setExternalFolio(e.target.value)} data-testid="sd-input-folio" placeholder="1234" />
            </div>
            <div>
              <Label>Fecha de emisión</Label>
              <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} data-testid="sd-input-date" />
            </div>
          </div>
          <div>
            <Label>UUID / Folio fiscal (opcional)</Label>
            <Input value={externalUuid} onChange={(e) => setExternalUuid(e.target.value)} data-testid="sd-input-uuid" placeholder="Copia el UUID del CFDI si lo tienes" className="mono text-xs" />
          </div>

          {/* Montos */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Subtotal</Label>
              <Input type="number" step="0.01" min="0" value={subtotal} onChange={(e) => setSubtotal(e.target.value)} data-testid="sd-input-subtotal" placeholder="0.00" />
            </div>
            <div>
              <Label>IVA</Label>
              <Input type="number" step="0.01" min="0" value={iva} onChange={(e) => setIva(e.target.value)} data-testid="sd-input-iva" placeholder="0.00" />
            </div>
            <div>
              <Label>Total</Label>
              <Input type="number" step="0.01" min="0" value={total} onChange={(e) => setTotal(e.target.value)} data-testid="sd-input-total" placeholder="0.00" />
            </div>
          </div>

          {isNC && (
            <div>
              <Label>Monto acreditado externamente</Label>
              <Input
                type="number" step="0.01" min="0"
                value={creditedAmount} onChange={(e) => setCreditedAmount(e.target.value)}
                data-testid="sd-input-credited" placeholder="0.00"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Dato solo informativo. <strong>No modifica la cuenta por pagar</strong>. Aplica el crédito con tu proveedor por fuera.
              </p>
            </div>
          )}

          {/* Archivos */}
          <div className="border border-dashed border-border rounded-lg p-4 space-y-3 bg-slate-50">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Archivos (al menos uno)</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>PDF</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="file" accept="application/pdf,.pdf"
                    onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                    data-testid="sd-input-pdf"
                  />
                  {pdfFile && (
                    <Button size="icon" variant="ghost" onClick={() => setPdfFile(null)} data-testid="sd-clear-pdf" type="button">
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {pdfFile && <p className="text-xs mt-1 text-muted-foreground">{pdfFile.name} · {(pdfFile.size / 1024).toFixed(1)} KB</p>}
              </div>
              <div>
                <Label>XML</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="file" accept="application/xml,text/xml,.xml"
                    onChange={(e) => setXmlFile(e.target.files?.[0] || null)}
                    data-testid="sd-input-xml"
                  />
                  {xmlFile && (
                    <Button size="icon" variant="ghost" onClick={() => setXmlFile(null)} data-testid="sd-clear-xml" type="button">
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {xmlFile && <p className="text-xs mt-1 text-muted-foreground">{xmlFile.name} · {(xmlFile.size / 1024).toFixed(1)} KB</p>}
              </div>
            </div>
          </div>

          {/* Vínculos opcionales */}
          <div className="border border-border rounded-lg p-4 space-y-3">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Vínculos administrativos (opcionales)</div>
            {!supplierId && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>Selecciona un proveedor primero para poder vincular a OC, CxP o devoluciones.</AlertDescription>
              </Alert>
            )}
            {supplierId && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Orden de compra</Label>
                  <Select value={purchaseOrderId || "none"} onValueChange={(v) => setPurchaseOrderId(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="sd-select-po"><SelectValue placeholder="Sin vincular" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin vincular</SelectItem>
                      {pos.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.folio} · {p.status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Cuenta por pagar</Label>
                  <Select value={accountsPayableId || "none"} onValueChange={(v) => setAccountsPayableId(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="sd-select-ap"><SelectValue placeholder="Sin vincular" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin vincular</SelectItem>
                      {aps.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.po_folio} · {a.status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Devolución</Label>
                  <Select value={purchaseReturnId || "none"} onValueChange={(v) => setPurchaseReturnId(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="sd-select-pr"><SelectValue placeholder="Sin vincular" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin vincular</SelectItem>
                      {returns.map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.folio} · {r.status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {isNC && (
              <Alert className="mt-2">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Esta nota de crédito quedará como <strong>evidencia documental</strong>. El sistema no ajusta cuentas por pagar
                  automáticamente. Si vinculas una devolución, la NC se muestra ligada solo con fines de trazabilidad.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <div>
            <Label>Notas</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="sd-input-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="sd-cancel">Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="sd-save" className="btn-primary-tenant">
            {saving ? "Guardando…" : "Guardar documento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
